import { useSession } from "next-auth/react";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";
import HostAnEventButton from "~/app/_components/HostAnEventButton";
import { UserPermissions } from "~/server/api/helpers/permissionHelpers";
import { CommunityPage } from "~/types/CommunityTypes";
import { hiddenCommunitySlugs } from "~/utils/constants";
import { If } from "../Conditional";
import { Button } from "../themed/Button";
import { FollowCommunityButtonBySlug } from "../community-components/CommunityHeader/FollowCommunityButton";

export const WantToHostEvent = ({
    community,
    permissions,
}: {
    community: Omit<CommunityPage, "leaders" | "topHosts">;
    permissions: UserPermissions;
}) => {
    const session = useSession();

    const [loaded, setLoaded] = useState(false);

    const searchParams = useSearchParams();

    useEffect(() => {
        const triggerPropose = searchParams?.get("newEvent") === "true";

        if (triggerPropose && session.data?.user && !loaded) {
            setLoaded(true);
        }
    }, [searchParams, session.data?.user, loaded]);

    const isAdminOrManager = permissions?.isAdmin || permissions?.isManager;
    const canCreateEvents =
        isAdminOrManager ||
        permissions?.isPreapprovedHost ||
        permissions.isChapterLeadForCommunity;

    // to hide the banner on certain communities
    if (hiddenCommunitySlugs.includes(community.slug) && !canCreateEvents) {
        return null;
    }

    // TODO: Delete after SXSW
    const hostCTALabelTMP =
        community.slug === "sxsw"
            ? "Host or list an event for Unofficial SXSW Calendar"
            : `Host an event for ${community.name}`;

    return (
        <div
            className={`
            flex max-h-[200px] min-h-[170px] w-full flex-col items-center
            justify-center gap-3 rounded-xl bg-[linear-gradient(135deg,rgba(96,46,244,1)0%,rgba(150,60,200,1)30%,rgba(150,60,200,1)45%,rgba(113,49,200,1)100%)]
            bg-[size:_160%] bg-[position:_0%_0%]
            p-4
            text-white transition-all duration-1000
            hover:bg-[position:_60%_60%] sm:max-h-[240px]
            `}
            style={{
                ...(community.bannersBg && { background: community.bannersBg }),
            }}
        >
            <h3 className="text-center text-[22px] leading-none">
                {hostCTALabelTMP}
            </h3>
            <div className="flex w-full max-w-[326px] flex-col items-center gap-2 md:max-w-[400px]">
                <div className="flex w-full flex-col items-center justify-center gap-2 md:flex-row">
                    <If
                        condition={
                            !session.data?.user && community.slug !== "sxsw"
                        }
                    >
                        <Link
                            href={`/signup?callbackUrl=${encodeURIComponent(
                                `/${community.slug}`,
                            )}`}
                            className="w-max"
                        >
                            <Button variant="tertiary" className="w-full">
                                Join River
                            </Button>
                        </Link>
                    </If>
                    <div className="flex flex-row items-center gap-2">
                        <HostAnEventButton
                            community={community}
                            permissions={permissions}
                        />

                        <If condition={community.slug === "sxsw"}>
                            <FollowCommunityButtonBySlug
                                communitySlug={community.slug}
                                variant="light-banner"
                                showEmailInput={false}
                            />
                        </If>
                    </div>
                </div>
                <If condition={!session.data?.user}>
                    <div>You must be logged in to propose events.</div>
                </If>
            </div>
        </div>
    );
};
